import type { LucideIcon } from 'lucide-react';
import {
  cloneElement,
  type ReactElement,
  type Ref,
  type RefObject,
  Suspense,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { lazyPart } from '@/lib/lazyPart';
import type { Align, Placement } from './internal/position';

/** A parte aberta (popover, lista, teclado) desce sob demanda, pedida no ócio (10.4). */
export const menuPanel = lazyPart(() => import('./MenuPanel').then((m) => m.MenuPanel));

export interface MenuItem {
  label: string;
  icon?: LucideIcon;
  onSelect(): void;
  destructive?: boolean;
  disabled?: boolean;
  /** Atalho ou detalhe à direita ("⌘K", "15:00"). */
  hint?: string;
  /** Item de escolha única (menuitemradio). */
  checked?: boolean;
  /** Itens de grupos diferentes ganham um separador entre si. */
  group?: string;
  /**
   * Abre uma página dentro do mesmo menu (chevron à direita, como os submenus do macOS e do iOS):
   * escolher o item não fecha o menu; `onSelect` troca a lista.
   */
  submenu?: boolean;
  /** Nome da página que o submenu abre: ao voltar dela, o foco retorna a este item. */
  opens?: string;
  /** Volta da página interna (chevron à esquerda); também não fecha o menu. */
  back?: boolean;
}

type TriggerProps = {
  ref?: Ref<HTMLElement>;
  onClick?: (e: unknown) => void;
  'aria-haspopup'?: 'menu';
  'aria-expanded'?: boolean;
  'aria-controls'?: string;
};

const LONG_PRESS_MS = 500;
const LONG_PRESS_SLOP = 10;

/**
 * Menu de contexto num elemento (linha, cartão): botão direito ou toque longo abrem o menu no
 * ponto do gesto. Devolve `true` em `consumeClick()` logo depois de um toque longo, para a linha
 * não abrir o detalhe no mesmo toque.
 */
function useContextGesture(
  target: RefObject<HTMLElement | null> | undefined,
  openAt: (p: { x: number; y: number }) => void,
) {
  const openRef = useRef(openAt);
  openRef.current = openAt;
  useEffect(() => {
    const el = target?.current;
    if (!el) return;
    let timer: number | undefined;
    let start: { x: number; y: number } | null = null;
    let fired = false;
    const cancel = () => {
      window.clearTimeout(timer);
      start = null;
    };
    const onContext = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest('input, textarea, [contenteditable="true"]')) return;
      e.preventDefault();
      cancel();
      openRef.current({ x: e.clientX, y: e.clientY });
    };
    const onDown = (e: PointerEvent) => {
      if (e.pointerType === 'mouse') return;
      fired = false;
      start = { x: e.clientX, y: e.clientY };
      const at = start;
      timer = window.setTimeout(() => {
        fired = true;
        start = null;
        openRef.current(at);
      }, LONG_PRESS_MS);
    };
    const onMove = (e: PointerEvent) => {
      if (start && Math.hypot(e.clientX - start.x, e.clientY - start.y) > LONG_PRESS_SLOP) cancel();
    };
    const onClick = (e: MouseEvent) => {
      if (!fired) return;
      fired = false;
      e.preventDefault();
      e.stopPropagation();
    };
    el.addEventListener('contextmenu', onContext);
    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', cancel);
    el.addEventListener('pointercancel', cancel);
    el.addEventListener('click', onClick, true);
    return () => {
      cancel();
      el.removeEventListener('contextmenu', onContext);
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', cancel);
      el.removeEventListener('pointercancel', cancel);
      el.removeEventListener('click', onClick, true);
    };
  }, [target]);
}

/**
 * Menu suspenso (SPEC 9.9). Setas, Home/End e busca por letra; Enter/Espaço seleciona;
 * Esc fecha e devolve o foco ao gatilho. Aqui ficam só o gatilho, o estado e o gesto de contexto;
 * a parte aberta (`MenuPanel`) desce sob demanda (10.4), pedida no ócio depois da 1ª pintura.
 *
 * Menu de contexto (4.0): `contextTarget` liga o botão direito e o toque longo (500 ms) daquele
 * elemento a este menu, que abre NO PONTO do gesto (`anchorPoint`), como no macOS e no iOS.
 * O gatilho "…" continua funcionando e recebe o foco de volta. Para controlar por fora:
 * `open` + `onOpenChange` + `anchorPoint`.
 */
export function Menu({
  trigger,
  items,
  label,
  placement = 'bottom',
  align = 'start',
  className,
  open: openProp,
  onOpenChange,
  anchorPoint: pointProp,
  contextTarget,
}: {
  trigger: ReactElement<TriggerProps>;
  items: MenuItem[];
  /** Nome acessível do menu (padrão: o texto do gatilho). */
  label?: string;
  placement?: Placement;
  align?: Align;
  className?: string;
  /** Aberto controlado (opcional). */
  open?: boolean;
  onOpenChange?(open: boolean): void;
  /** Abre no ponto da tela em vez de sob o gatilho (controlado). */
  anchorPoint?: { x: number; y: number } | null;
  /** Elemento cujo botão direito / toque longo abre este menu no ponto do gesto. */
  contextTarget?: RefObject<HTMLElement | null>;
}) {
  const id = useId();
  const anchor = useRef<HTMLElement>(null);
  const [openState, setOpenState] = useState(false);
  const [point, setPoint] = useState<{ x: number; y: number } | null>(null);
  const open = openProp ?? openState;
  const setOpen = (next: boolean | ((o: boolean) => boolean)) => {
    const v = typeof next === 'function' ? next(open) : next;
    if (!v) setPoint(null);
    if (openProp === undefined) setOpenState(v);
    onOpenChange?.(v);
  };
  useContextGesture(contextTarget, (p) => {
    setPoint(p);
    if (openProp === undefined) setOpenState(true);
    onOpenChange?.(true);
  });
  const at = pointProp ?? point;
  // Montado a partir da 1ª abertura e mantido (a saída anima; a 2ª abertura é instantânea).
  const [seen, setSeen] = useState(open);
  if (open && !seen) setSeen(true);
  useEffect(() => menuPanel.preloadWhenIdle(), []);

  const triggerEl = cloneElement(trigger, {
    ref: anchor,
    onClick: () => {
      setPoint(null);
      setOpen((o) => !o);
    },
    'aria-haspopup': 'menu',
    'aria-expanded': open,
    'aria-controls': open ? id : undefined,
  });

  return (
    <>
      {triggerEl}
      {(open || seen) && (
        <Suspense fallback={null}>
          <menuPanel.Part
            anchor={anchor}
            open={open}
            onClose={() => setOpen(false)}
            items={items}
            id={id}
            label={label}
            placement={placement}
            align={align}
            anchorPoint={at}
            className={className}
          />
        </Suspense>
      )}
    </>
  );
}
